import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { writeAuditLog } from "@/lib/supabase-helpers";
import { useToast } from "@/hooks/use-toast";

export interface QueuedAction {
  id: string;
  table: string;
  operation: "insert" | "update";
  payload: Record<string, unknown>;
  createdAt: string;
  retryCount: number;
  lastError?: string;
  auditMeta?: {
    action: string;
    entity_type: string;
  };
}

interface OfflineContextType {
  isOffline: boolean;
  queue: QueuedAction[];
  enqueue: (action: Omit<QueuedAction, "id" | "createdAt" | "retryCount">) => void;
  syncNow: () => Promise<void>;
  syncing: boolean;
  removeFromQueue: (id: string) => void;
  clearQueue: () => void;
  lastSyncAt: string | null;
}

const QUEUE_KEY = "dsg_offline_queue";
const LAST_SYNC_KEY = "dsg_last_sync";
const MAX_RETRIES = 5;

function loadQueue(): QueuedAction[] {
  try {
    const items = JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
    // Migrate old items without retryCount
    return items.map((item: any) => ({ ...item, retryCount: item.retryCount ?? 0 }));
  } catch {
    return [];
  }
}

function saveQueue(q: QueuedAction[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

const OfflineContext = createContext<OfflineContextType>({
  isOffline: false,
  queue: [],
  enqueue: () => {},
  syncNow: async () => {},
  syncing: false,
  removeFromQueue: () => {},
  clearQueue: () => {},
  lastSyncAt: null,
});

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queue, setQueue] = useState<QueuedAction[]>(loadQueue);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(
    localStorage.getItem(LAST_SYNC_KEY)
  );
  const { toast } = useToast();

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  const enqueue = useCallback((action: Omit<QueuedAction, "id" | "createdAt" | "retryCount">) => {
    const item: QueuedAction = {
      ...action,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };
    setQueue((prev) => {
      const next = [...prev, item];
      saveQueue(next);
      return next;
    });
    toast({ title: "📴 Saved offline", description: `Will sync when you're back online. (${action.table})` });
  }, [toast]);

  const removeFromQueue = useCallback((id: string) => {
    setQueue((prev) => {
      const next = prev.filter((item) => item.id !== id);
      saveQueue(next);
      return next;
    });
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    saveQueue([]);
  }, []);

  const syncNow = useCallback(async () => {
    const pending = loadQueue();
    if (pending.length === 0) return;
    setSyncing(true);
    const failed: QueuedAction[] = [];
    let successCount = 0;

    for (const item of pending) {
      // Skip items that have exceeded max retries
      if (item.retryCount >= MAX_RETRIES) {
        failed.push(item);
        continue;
      }

      try {
        let result: { data: any; error: any };
        if (item.operation === "insert") {
          // Remove any offline- prefixed IDs before inserting
          const payload = { ...item.payload };
          if (typeof payload.id === "string" && payload.id.startsWith("offline-")) {
            delete payload.id;
          }
          result = await (supabase.from(item.table as any) as any).insert(payload).select().single();
        } else {
          const { id, ...rest } = item.payload;
          result = await (supabase.from(item.table as any) as any).update(rest).eq("id", id);
        }

        if (result.error) {
          // Check if it's a duplicate / conflict (already synced)
          if (result.error.code === "23505") {
            // Duplicate key — item already exists, treat as success
            successCount++;
          } else {
            failed.push({
              ...item,
              retryCount: item.retryCount + 1,
              lastError: result.error.message,
            });
          }
        } else {
          successCount++;
          if (item.auditMeta) {
            await writeAuditLog({
              action: item.auditMeta.action,
              entity_type: item.auditMeta.entity_type,
              entity_id: result.data?.id ?? (item.payload.id as string),
              after_value: result.data ?? item.payload,
              reason: "synced from offline queue",
            });
          }
        }
      } catch {
        failed.push({
          ...item,
          retryCount: item.retryCount + 1,
          lastError: "Network error",
        });
      }

      // Small delay between operations to avoid overwhelming the API
      if (pending.length > 1) {
        await new Promise((r) => setTimeout(r, 200));
      }
    }

    saveQueue(failed);
    setQueue(failed);
    setSyncing(false);

    const now = new Date().toISOString();
    setLastSyncAt(now);
    localStorage.setItem(LAST_SYNC_KEY, now);

    if (successCount > 0) {
      await writeAuditLog({
        action: "offline.sync",
        entity_type: "system",
        after_value: { synced: successCount, failed: failed.length },
      });

      const permanentlyFailed = failed.filter((f) => f.retryCount >= MAX_RETRIES);
      toast({
        title: `✅ Synced ${successCount} item${successCount > 1 ? "s" : ""}`,
        description: failed.length
          ? `${failed.length} pending retry${permanentlyFailed.length ? `, ${permanentlyFailed.length} permanently failed` : ""}.`
          : "All items synced successfully!",
      });
    } else if (failed.length > 0) {
      toast({
        title: "⚠️ Sync issues",
        description: `${failed.length} item${failed.length > 1 ? "s" : ""} could not be synced. Will retry automatically.`,
      });
    }
  }, [toast]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (!isOffline && queue.length > 0) {
      syncNow();
    }
  }, [isOffline]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic retry every 60s when online and items are queued
  useEffect(() => {
    if (isOffline || queue.length === 0) return;
    const interval = setInterval(() => {
      const retryable = queue.filter((q) => q.retryCount < MAX_RETRIES);
      if (retryable.length > 0 && !syncing) {
        syncNow();
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [isOffline, queue.length, syncing, syncNow, queue]);

  return (
    <OfflineContext.Provider value={{ isOffline, queue, enqueue, syncNow, syncing, removeFromQueue, clearQueue, lastSyncAt }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineContext);
}
